import {Component, OnDestroy, OnInit, Signal, ViewEncapsulation, WritableSignal, inject, linkedSignal, signal} from '@angular/core';
import {NgClass} from "@angular/common";
import {NavigationEnd, Router} from "@angular/router";
import {IconBadgeComponent} from "../../../shared/elements/icon-badge/icon-badge.component";
import {TranslatePipe} from "../../../shared/pipes/translate.pipe";
import {LinkComponent} from "../../../shared/elements/link/link.component";
import {RouterService} from "../../../shared/services/router.service";
import {FormButtonComponent} from "../../../shared/elements/form-button/form-button.component";
import {Subscription, take} from "rxjs";
import {LanguageService} from "../../../shared/services/language.service";
import {NotificationComponent} from "../../../shared/elements/notification/notification.component";
import {HeadlineComponent} from "../../../shared/elements/headline/headline.component";
import {DropDownListModule} from "@syncfusion/ej2-angular-dropdowns";
import {DividerDateComponent} from "../../../shared/elements/divider-date/divider-date.component";
import {FormDropdownComponent} from "../../../shared/elements/form-dropdown/form-dropdown.component";
import {FormBuilder, FormGroup} from "@angular/forms";
import {DialogProfileLanguageComponent} from "../../profile/dialog-profile-language/dialog-profile-language.component";
import {DialogProfilePushComponent} from "../../profile/dialog-profile-push/dialog-profile-push.component";
import {TooltipComponent} from "../../../shared/elements/tooltip/tooltip.component";
import {AvatarComponent} from "../../../shared/elements/avatar/avatar.component";
import {LayoutService} from "../../../shared/services/layout.service";
import {EnvironmentService} from "../../../shared/services/environment.service";
import {CustomerService} from "../../../shared/services/customer.service";
import {SocketService} from "../../../shared/services/socket.service";
import {ToasterService} from "../../../shared/services/toaster.service";
import {ProfileService} from '../../../shared/services/profile.service';
import {WarningComponent} from "../../../shared/elements/warning/warning.component";
import {FirebaseService} from '../../../shared/services/firebase.service';
import {NotificationChannel} from '../../../shared/types/notifications-types/notificationChannel';
import {NotificationResource} from '../../../shared/types/notifications-types/notificationResource';
import {NotificationReadState} from '../../../shared/types/notifications-types/notificationReadState';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {NavService} from '../../../shared/services/nav.service';
import {NotificationStateService} from '../../../shared/services/notification-state.service';
import {AppDownloadDialogComponent} from "../app-download-dialog/app-download-dialog.component";
import {AssistanceDialogComponent} from "../assistance-dialog/assistance-dialog.component";
import {IconBarPolicy, IconBarService} from '../../../shared/services/icon-bar.service';
import {ApiService} from '../../../shared/services/api.service';
import {CapabilitiesService} from '../../../shared/services/capabilities.service';
import {TokenService} from '../../../shared/services/token.service';
import {AppStoreResetService} from '../../../shared/services/app-store-reset.service';
import {DialogHomeIconsComponent} from "../../home/dialog-home-icons/dialog-home-icons.component";

@Component({
    selector: 'app-bar-top',
    imports: [
    IconBadgeComponent,
    TranslatePipe,
    LinkComponent,
    FormButtonComponent,
    NotificationComponent,
    HeadlineComponent,
    DropDownListModule,
    DividerDateComponent,
    FormDropdownComponent,
    DialogProfileLanguageComponent,
    DialogProfilePushComponent,
    TooltipComponent,
    AvatarComponent,
    NgClass,
    WarningComponent,
    AppDownloadDialogComponent,
    DialogHomeIconsComponent,
    AssistanceDialogComponent
],
    templateUrl: './bar-top.component.html',
    styleUrl: './bar-top.component.scss',
    encapsulation: ViewEncapsulation.None
})
export class BarTopComponent implements OnInit, OnDestroy {
  public barIconService: IconBarService = inject(IconBarService);
  public routerService: RouterService = inject(RouterService);
  public layoutService: LayoutService = inject(LayoutService);
  public profileService: ProfileService = inject(ProfileService);
  public firebaseService: FirebaseService = inject(FirebaseService);
  public capabilities: CapabilitiesService = inject(CapabilitiesService);
  public customerService: CustomerService = inject(CustomerService);
  private notificationStateService: NotificationStateService = inject(NotificationStateService);
  private languageService: LanguageService = inject(LanguageService);
  private formBuilder: FormBuilder = inject(FormBuilder);
  private environmentService: EnvironmentService = inject(EnvironmentService);
  private socketService: SocketService = inject(SocketService);
  private toasterService: ToasterService = inject(ToasterService);
  private apiService: ApiService = inject(ApiService);
  private navService: NavService = inject(NavService);
  private tokenService: TokenService = inject(TokenService);
  private appStoreResetService: AppStoreResetService = inject(AppStoreResetService);
  private router: Router = inject(Router);


  public tab: 'notifications' | 'chat' | null = null;
  public status: NotificationReadState = NotificationReadState.Unread;
  public module: 'calendar' | 'channel' | 'chat' | 'intranet' | 'mapp' | 'meeting' | 'survey' | 'workflow' | null = null;
  public profileLanguageDialog: boolean = false;
  public profilePushDialog: boolean = false;
  public appDownloadDialog: boolean = false;
  public assistanceDialog: boolean = false;
  public notifications: Signal<NotificationResource[]> = this.notificationStateService.barTopNotifications;
  public iconBarPolicy: IconBarPolicy = IconBarPolicy.Toggleable;
  public currentBreakpoint: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl' = 'xs';
  public homeIconsDialog: WritableSignal<boolean> = signal<boolean>(false);

  private subscriptions: Subscription[] = [];

  public activeChannels: Signal<{id: string, name: string}[]> = linkedSignal(() => {
    const mappedModules = [
      {id: 'all', name: this.languageService.getTranslationByKey('ALLNOTIFICATIONS')},
      {id: NotificationChannel.User, name: this.languageService.getTranslationByKey('USER')}
    ];
    const menuNamesByType = new Map<string, string>(
      this.navService.userMenu().map((menu) => [menu.type, menu.name])
    );
    const badgeChannelKeys = this.getBadgeChannelKeysExcludingChat();
    badgeChannelKeys.forEach((channel) => {
      if (channel === NotificationChannel.User) {
        return;
      }

      mappedModules.push({
        id: channel,
        name: menuNamesByType.get(channel) ?? this.languageService.getTranslationByKey(channel.toUpperCase())
      });
    });

    return mappedModules;
  });

  public filterForm: FormGroup = this.formBuilder.group({
    module: ['all']
  });

  public constructor() {
    this.layoutService.scrolledToEnd
    .pipe(takeUntilDestroyed())
    .subscribe({
      next: (scrolledToEnd: boolean | string) => {
        if (scrolledToEnd && scrolledToEnd === 'bar-top') {
          this.loadList();
        }
      }
    })

    this.filterForm.get('module')?.valueChanges
    .pipe(takeUntilDestroyed())
    .subscribe(() => {
      this.notificationStateService.clearBarTopNotifications();
      this.loadList();
    });

    this.barIconService.iconBarPolicy
    .pipe(takeUntilDestroyed())
    .subscribe((policy) => {
      this.iconBarPolicy = policy;
    });

    this.router.events
    .pipe(takeUntilDestroyed())
    .subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.ensureIconBarToggleableOutsideHome(event.urlAfterRedirects);
      }
    });

    this.ensureIconBarToggleableOutsideHome(this.router.url);

    this.layoutService.breakpointWidthType
    .pipe(takeUntilDestroyed())
    .subscribe((breakpoint) => {
      this.currentBreakpoint = breakpoint;
    });
  }


  public ngOnInit(): void {
    this.subscriptions.push(this.socketService.reminder.subscribe(reminderUpdate => {
      this.toasterService.reminder(reminderUpdate);
    }));
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach((subscription: Subscription) => subscription.unsubscribe());
  }

  public signOut(): void {
    this.apiService.logoutUser()
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.tokenService.clearToken();
          this.profileService.resetProfile();
          this.appStoreResetService.resetAllStores();
          const targetRoute = this.customerService.hasSsoOptions() ? '/auth/sso' : '/auth/login';
          this.routerService.navigateTo(targetRoute);
        },
        error: () => {
          this.tokenService.clearToken();
          this.profileService.resetProfile();
          this.appStoreResetService.resetAllStores();
          const targetRoute = this.customerService.hasSsoOptions() ? '/auth/sso' : '/auth/login';
          this.routerService.navigateTo(targetRoute);
        }
      });
  }

  public setTab(tab: 'notifications' | 'chat'): void {
    this.tab = (tab === this.tab ? null : tab);
    this.status = NotificationReadState.Unread;
    this.module = (this.tab === 'chat' ? 'chat' : null);
    if (this.tab) {
      this.notificationStateService.clearBarTopNotifications();
      this.loadList();
    }
  }

  public closeTab(): void {
    this.tab = null;
  }

  public setStatus(status: NotificationReadState): void {
    this.status = status;
    this.notificationStateService.clearBarTopNotifications();
    this.loadList();
  }

  public hasNonChatBadgeAlert(): boolean {
    return this.notificationStateService.hasNonChatBadgeAlert();
  }

  public get iconsBadges(): Map<string, boolean> {
    return this.notificationStateService.iconsBadges();
  }

  public loadList(): void {
    if (!this.tab) {
      return;
    }

    const channels: NotificationChannel[] = []
    if(this.tab === 'notifications') {
      if(this.filterForm.get('module')?.value === 'all') {
        channels.push(...this.getChannelsForAllNotificationsSelection());
      } else {
        channels.push((this.filterForm.get('module')?.value) as NotificationChannel);
      }
    } else {
      channels.push(NotificationChannel.Chat);
    }

    this.notificationStateService.loadBarTopNotifications(
      this.status,
      channels,
      true,
      25,
      (err) => {
        console.log(err);
      }
    );
  }

  public shouldShowNoNotificationsMessage(): boolean {
    if (!this.tab || this.notifications().length > 0) {
      return false;
    }

    if (this.tab === 'chat') {
      return !(this.iconsBadges.get(NotificationChannel.Chat) ?? false);
    }

    const selectedModule = this.filterForm.get('module')?.value;
    if (selectedModule === 'all') {
      return !this.hasNonChatBadgeAlert();
    }

    if (typeof selectedModule !== 'string') {
      return true;
    }

    return !(this.iconsBadges.get(selectedModule) ?? false);
  }

  public switchEditing(): void {
    this.environmentService.setEditing(!this.environmentService.getEditing());
  }

  public toggleAutomaticTranslation(): void {
    this.profileService.setProfileAutoTranslation(!this.profileService.profile()?.user.auto_translation);
  }

  public isHomeRoute(): boolean {
    return this.router.url.startsWith('/home');
  }

  public shouldShowIconBarToggle(): boolean {
    if (this.currentBreakpoint === 'xs' || this.currentBreakpoint === 'sm') {
      return false;
    }
    if (!this.isHomeRoute()) {
      return true;
    }

    if(this.isHomeRoute()) {
      return false;
    }

    return this.iconBarPolicy === IconBarPolicy.Toggleable;
  }

  public markNotificationRead(notificationId: number): void {
    this.notificationStateService.markNotificationRead(
      notificationId,
      (err) => {
        console.error(err);
      }
    );
  }

  private ensureIconBarToggleableOutsideHome(url: string): void {
    if (!url.startsWith('/home')) {
      this.barIconService.setIconBarPolicy(IconBarPolicy.Toggleable);
    }
  }

  public markNotificationUnread(notificationId: number): void {
    this.notificationStateService.markNotificationUnread(
      notificationId,
      (err) => {
        console.error(err);
      }
    );
  }

  public setRead(notificationTab: 'notifications' | 'chat'): void {
    const channelsToSetAsRead: NotificationChannel[] = [];
    if (notificationTab === 'chat') {
      channelsToSetAsRead.push(NotificationChannel.Chat);
    } else {
      if(this.filterForm.get('module')?.value === 'all') {
        channelsToSetAsRead.push(...this.getChannelsForAllNotificationsSelection());
      } else {
        channelsToSetAsRead.push((this.filterForm.get('module')?.value) as NotificationChannel);
      }
    }

    this.notificationStateService.setAllReadForChannels(
      channelsToSetAsRead,
      () => {
        this.notificationStateService.clearBarTopNotifications();
        this.loadList();
      },
      (err) => {
        console.error(err);
      }
    );
  }

  public setUnread(notificationTab: 'notifications' | 'chat'): void {
    if (notificationTab !== 'notifications') {
      return;
    }

    const notificationIds = this.notifications().map((notification) => notification.id);
    this.notificationStateService.setAllUnreadByIds(
      notificationIds,
      () => {
        this.notificationStateService.clearBarTopNotifications();
        this.loadList();
      },
      (err) => {
        console.error(err);
      }
    );
  }

  public shouldShowNotificationDateDivider(index: number): boolean {
    if (index === 0) {
      return true;
    }

    const items = this.notifications();
    const current = items[index];
    const previous = items[index - 1];

    if (!current || !previous) {
      return false;
    }

    const currentDate = new Date(current.created_at);
    const previousDate = new Date(previous.created_at);

    return (
      currentDate.getFullYear() !== previousDate.getFullYear() ||
      currentDate.getMonth() !== previousDate.getMonth() ||
      currentDate.getDate() !== previousDate.getDate()
    );
  }

  public formatDividerDate(item: NotificationResource): Date {
    return new Date(item.created_at);
  }

  private getBadgeChannelKeysExcludingChat(): string[] {
    const channelKeys = Array.from(this.iconsBadges.keys())
      .filter((key) => key !== NotificationChannel.Chat);
    return Array.from(new Set(channelKeys));
  }

  public openPlatformInfo(): void {
    this.routerService.navigateTo('/platform-info');
  }

  public openAssist(): void {
    window.open('https://assist.humanstars.app', '_blank');
  }

  private getChannelsForAllNotificationsSelection(): NotificationChannel[] {
    const badgeChannels = this.getBadgeChannelKeysExcludingChat();
    const channels: string[] = [];

    if (badgeChannels.includes(NotificationChannel.User)) {
      channels.push(NotificationChannel.User);
    }

    channels.push(...badgeChannels.filter((channel) => channel !== NotificationChannel.User));
    return channels as NotificationChannel[];
  }
}
